import React from 'react';
import {Size} from '../types';
import {
  ButtonAction,
  ModalHeaderIconButton,
  LeftFooterAction,
  LeftFooterButton,
  LeftFooterHyperlink,
  ModalHeaderButtons
} from './ModalTypes';
import {ModalContainer} from './index';
import styled, { useTheme } from 'styled-components';
import {
  COLORS,
  ComponentLStyling,
  ComponentMStyling, ComponentSStyling,
  ComponentTextStyle, ComponentXLStyling,
  ComponentXSStyling,
  ComponentXXSStyling, Note,
  scrollBarStyling
} from '../styles';
import {TooltipContainer, TooltipWrapper} from '../Tooltips';
import {TextButton, IconButton} from '../Button';
import {SystemIcons} from '../icons';
import {HyperLink} from '../HyperLink';
import {ImageWithFallbacksProps} from "../Image/ImageWithFallbacks";
import {DropdownButton} from "../Dropdown";
import {ToggleButton} from "../Toggles";
import {ImageWithFallbacks} from "../Image";

const HeaderText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: ${props => COLORS.generateToken({componentType:'text', defaultVariant:'default'}, props.theme)};

  padding: 12px 0 12px 12px;
  gap: 4px;

  &:not(:first-child) {
    padding-left: 0 !important;
  }

`;
const HeaderTitle = styled.div`
  ${props => ComponentMStyling(ComponentTextStyle.Bold, COLORS.generateToken({ componentType:'text', defaultVariant:'default' }, props.theme))}
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
`;

const HeaderWithImageActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: absolute;

  button:only-child {
    margin-left: auto;
  }

  top: 4px;
  left: 4px;
  right: 4px;

  .medium & {
    top: 12px;
    left: 12px;
    right: 12px;
  }

  .large & {
    top: 20px;
    left: 20px;
    right: 20px;
  }
`;

const HeaderWithImage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  img {
    object-fit: cover;
    width: 100%;
    height: 160px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  .medium & {
    img {
      height: 200px;
    }
  }

  .large & {
    img {
      height: 240px;
    }
  }
`;

export const ModalContentHeaderComponent = styled.div`
  display: flex;
  flex-direction: row;
  padding: 4px;

  ${TooltipContainer} {
    height: max-content;
  }
`;

export const ModalContentBodyComponent = styled.div<{$scrollable?: boolean}>`
  margin-right: 8px;
  padding: 0 8px 0 16px;
  overflow: ${props => props.$scrollable ? 'auto' : 'visible'};

  ${scrollBarStyling(Size.Small)}
`;

const ModalContentFooterLeftContainerComponent = styled.div`
  display: flex;
  align-items: center;
`;

const ModalContentFooterLeftNoteComponent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  ${props => ComponentXXSStyling(ComponentTextStyle.Bold, COLORS.generateToken({componentType:'text', defaultVariant: 'subtle'}, props.theme))}
`;

const ModalContentFooterLeftLinkComponent = styled.div`
  ${props => ComponentSStyling(ComponentTextStyle.Bold, COLORS.generateToken({ componentType: 'text-link', defaultVariant: 'default' }, props.theme))}
  a {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }
`;

const FooterRightContainer = styled.div`
  gap: 8px;
  margin-left: auto;
`

export const ModalContentFooterComponent = styled.div`
  display: flex;
  flex-direction: row;
  padding: 8px 16px;
`;


const Wrapper = styled.div`  
  display: flex;
  flex-direction: column;
  width: max-content;

  max-height: calc(100vh - 32px);
  max-width: calc(100vw - 32px);

  &.medium {
    max-height: calc(100vh - 64px);
    max-width: calc(100vw - 64px);

    ${ModalContentHeaderComponent} {
      padding: 12px 12px 4px 12px;

      ${HeaderText} {
        padding: 12px 0 12px 12px;
        gap: 4px;

        ${HeaderTitle} {
          ${ComponentLStyling(ComponentTextStyle.Bold, null)}
        }
      }
    }

    ${ModalContentBodyComponent} {
      padding: 0 16px 0 24px;
    }

    ${ModalContentFooterComponent} {
      padding: 12px 24px 20px 24px;

      ${ModalContentFooterLeftNoteComponent} {
        ${ComponentXSStyling(ComponentTextStyle.Bold, null)}
        gap: 6px;
      }

      ${ModalContentFooterLeftLinkComponent} {
        a {
          gap: 6px;
        }

        ${ComponentMStyling(ComponentTextStyle.Bold, null)}
      }

      ${FooterRightContainer} {
        gap: 8px;
      }
    }
  }

  &.large {
    max-height: calc(100vh - 112px);
    max-width: calc(100vw - 112px);

    ${ModalContentHeaderComponent} {
      padding: 20px 20px 4px 20px;

      ${HeaderText} {
        padding: 10px 0 10px 12px;
        gap: 8px;

        ${HeaderTitle} {
          ${ComponentXLStyling(ComponentTextStyle.Bold, null)}
        }
      }
    }

    ${ModalContentBodyComponent} {
      padding: 0 24px 0 32px;
    }

    ${ModalContentFooterComponent} {
      padding: 16px 32px 32px 32px;

      ${ModalContentFooterLeftNoteComponent} {
        ${ComponentSStyling(ComponentTextStyle.Bold, null)}
        gap: 8px;
      }

      ${ModalContentFooterLeftLinkComponent} {
        a {
          gap: 8px;
        }

        ${ComponentLStyling(ComponentTextStyle.Bold, null)}
      }

      ${FooterRightContainer} {
        gap: 16px;
      }
    }
  }
`;


export interface ModalContentProps {
  size?: Size.Small | Size.Medium | Size.Large;
  isModalOpen: boolean;
  closeAction: () => void;
  hideCloseButton?: boolean;
  onBack?: () => void;
  title: string;
  note?: string;
  image?: ImageWithFallbacksProps;
  headerActions?: ModalHeaderButtons[];
  footerActions?: ButtonAction[];
  scrollableBody?: boolean;
  leftFooterAction?: LeftFooterAction;
  tooltip?: string;
  zIndex?: number;
  children?: React.ReactNode;
}

const ModalContent: React.FC<ModalContentProps> = (props) => {
  const theme = useTheme();

  const tooltip = () =>
    !!props.tooltip &&
    <TooltipWrapper delay="0s" size={Size.XSmall} align="center" position="bottom" withArrow={false}
                    label={props.tooltip}>
      <IconButton variant="secondary" shape="circular" style={{cursor: 'help'}} action={() => {
      }}>
        <SystemIcons.Help size="24px" color={COLORS.generateToken({componentType:'icon', defaultVariant:'subtle'}, theme)}/>
      </IconButton>
    </TooltipWrapper>;

  const headerActions = () => props.headerActions?.map((action, index) => {
      switch (action.componentType) {
        case 'icon': {
          const {icon, componentType, ...rest} = action;
          return <IconButton key={action.id || index}
                             {...rest}>
            {icon}
          </IconButton>
        }
        case 'dropdown': {
          const {componentType, ...rest} = action;
          return <DropdownButton type={'icon'} size={props.size} {...rest}/>
        }
        case 'toggle': {
          const {componentType, ...rest} = action;
          return <ToggleButton  {...rest}/>
        }
      }
    }
  );

  const getLeftActionIconElement = () =>
    !!props.leftFooterAction?.icon &&
    React.cloneElement(props.leftFooterAction?.icon as React.ReactElement<any>, {
      size:
        props.size === Size.Small
          ? '20px'
          : props.size === Size.Large
            ? '28px'
            : '24px'
    });

  const leftFooterAction = () => {
    if (!props.leftFooterAction) return null;

    switch (props.leftFooterAction.actionType) {
      case 'button': {
        const {text, variant, action, ...rest} = props.leftFooterAction as LeftFooterButton;
        return (
          <TextButton{...rest}
                 size={props.size}
                 onClick={action}
                 variant={variant ?? 'secondary'}>
            {text}
          </TextButton>
        );
      }
      case 'hyperlink': {
        const {text, variant, action, actionType, ...rest} = props.leftFooterAction as LeftFooterHyperlink;
        return (
          <ModalContentFooterLeftLinkComponent>
            <HyperLink {...rest}
                       variant={variant ?? 'default'}
                       onClick={(e) => {
                         if (action) {
                           e.preventDefault();
                           action(e);
                         }
                       }}>
              {text}
              {getLeftActionIconElement()}
            </HyperLink>
          </ModalContentFooterLeftLinkComponent>
        );

      }
      case 'note': {
        const {text} = props.leftFooterAction;
        return (
          <ModalContentFooterLeftNoteComponent>
            {getLeftActionIconElement()}
            <span>{text}</span>
          </ModalContentFooterLeftNoteComponent>
        );
      }
    }
  };


  return (
    <ModalContainer showModal={props.isModalOpen} closeModal={props.closeAction}>
      <Wrapper className={props.size ?? Size.Medium}>
        {
          props.image &&
          <HeaderWithImage>
            <ImageWithFallbacks {...props.image}/>
            <HeaderWithImageActions>
              {
                props.onBack &&
                <IconButton variant="secondary" shape="circular" action={props.onBack}>
                  <SystemIcons.ArrowLineLeft/>
                </IconButton>

              }
              {!props.hideCloseButton && <IconButton dataTestId={'content-modal-close-button'} id={'content-modal-close-button'}
                          variant="secondary"
                          shape="circular"
                          action={props.closeAction}>
                <SystemIcons.Close size="24px" color={COLORS.generateToken({componentType:'icon', defaultVariant:'subtle'}, theme)}/>
              </IconButton>}
            </HeaderWithImageActions>

            <ModalContentHeaderComponent>
              <HeaderText>
                <HeaderTitle>{props.title}</HeaderTitle>
                {!!props.note && <Note>{props.note}</Note>}
              </HeaderText>
              <HeaderActions>
                {tooltip()}
                {headerActions()}
              </HeaderActions>
            </ModalContentHeaderComponent>
          </HeaderWithImage>
        }
        {
          !props.image &&
          <ModalContentHeaderComponent>
            {
              props.onBack &&
              <IconButton variant="secondary" shape="circular" action={props.onBack}>
                <SystemIcons.ArrowLineLeft/>
              </IconButton>
            }
            <HeaderText>
              <HeaderTitle>{props.title}</HeaderTitle>
              {!!props.note && <Note>{props.note}</Note>}
            </HeaderText>
            <HeaderActions>
              {tooltip()}
              {headerActions()}
              {!props.hideCloseButton && <IconButton dataTestId={'content-modal-close-button'} id={'content-modal-close-button'}
                    variant="secondary"
                    shape="circular"
                    action={props.closeAction}>
              <SystemIcons.Close size="24px" color={COLORS.generateToken({componentType:'icon', defaultVariant:'subtle'}, theme)}/>
              </IconButton>}
            </HeaderActions>
          </ModalContentHeaderComponent>
        }
        <ModalContentBodyComponent $scrollable={props.scrollableBody == undefined ? true : props.scrollableBody}>
          {props.children}
        </ModalContentBodyComponent>
        <ModalContentFooterComponent>
          <ModalContentFooterLeftContainerComponent>
            {leftFooterAction()}
          </ModalContentFooterLeftContainerComponent>
          <FooterRightContainer>
            {props.footerActions?.map((item, index) => {
                const {action, text, ...rest} = item;
                return <TextButton key={rest.id || index}
                               {...rest}
                               onClick={action}
                               size={props.size}
                               onKeyDown={a => (a.key === 'Enter' || a.key === ' ') && action!(a)}>
                  {text}
                </TextButton>
              }
            )}
          </FooterRightContainer>
        </ModalContentFooterComponent>
      </Wrapper>
    </ModalContainer>
  );
};

export default ModalContent;
